interface PageTypeRule {
  pageType: string;
  matches: (parts: string[]) => boolean;
  extract?: (parts: string[]) => Record<string, unknown>;
}

// Referenced below by the .specs-row-box DOM-scrape fallback too, so both
// detection paths agree on the exact string.
const BOAT_DETAIL_PAGE_TYPE = 'boat_detail';

// URL-path-based page type detection. Add a new page type here as one
// entry rather than a new try/catch block — parts is computed once and
// the first matching rule wins.
const PAGE_TYPE_RULES: PageTypeRule[] = [
  {
    // /details/{make}/{model}/{inventoryId}
    pageType: BOAT_DETAIL_PAGE_TYPE,
    matches: (parts) => parts[0] === 'details' && parts.length >= 4,
    extract: (parts) => ({ inventoryId: parts[parts.length - 1] }),
  },
  {
    // /service-{location-slug}, e.g. /service-lewisville-tx. The location
    // slug varies per dealer/location, so this matches on the "service"
    // prefix rather than an exact segment.
    pageType: 'service',
    matches: (parts) => parts[0]?.startsWith('service') ?? false,
  },
  {
    // /search — the inventory search/listing page.
    pageType: 'search',
    matches: (parts) => parts[0] === 'search',
  },
];

window.AudienticContextAdapter = function (): Record<string, unknown> {
  const context: Record<string, unknown> = {};

  context.dealerBaseUrl = window.location.origin;

  // 1. URL pattern page type detection — see PAGE_TYPE_RULES above.
  try {
    const parts = window.location.pathname.split('/').filter(Boolean);
    const rule = PAGE_TYPE_RULES.find((r) => r.matches(parts));
    if (rule) {
      context.pageType = rule.pageType;
      Object.assign(context, rule.extract?.(parts));
    }
  } catch (_) {}

  // 2. DOM scrape of .specs-row-box — boat detail pages only
  try {
    const fieldMap: Record<string, string> = {
      'Stock Number': 'stockNumber',
      Make: 'make',
      Model: 'model',
      Year: 'year',
      Condition: 'condition',
      Category: 'category',
      Location: 'location',
    };
    const items = document.querySelectorAll('.specs-row-box .specs-item');
    items.forEach((item) => {
      const nameEl = item.querySelector('.specs-name');
      const valueEl = item.querySelector('.specs-value');
      if (!nameEl || !valueEl) return;
      const key = fieldMap[nameEl.textContent?.trim() ?? ''];
      const value = valueEl.textContent?.trim();
      if (key && value) context[key] = value;
    });
    if (items.length > 0) context.pageType = BOAT_DETAIL_PAGE_TYPE;
  } catch (_) {}

  // 3. localStorage.selectedStore — fills in missing store context
  try {
    const raw = localStorage.getItem('selectedStore');
    const store = raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
    if (store) {
      if (!context.dealership && store.location_name) context.dealership = store.location_name;
      if (!context.location && store.city && store.state)
        context.location = `${store.city}, ${store.state}`;

      if (store.address_1) {
        context.locationAddress = store.address_2
          ? `${store.address_1} ${store.address_2}`
          : String(store.address_1);
      }
      if (store.city && store.state && store.postal)
        context.locationCityStateZip = `${store.city}, ${store.state} ${store.postal}`;
      if (store.phone) context.locationPhone = String(store.phone);
      // Dealer's own location code (e.g. "ATL") — surfaced as a token so a
      // per-widget webhook body template (#114) can populate a receiving
      // system's location/dealer-code field per active store.
      if (store.dealer_id) context.dealerId = String(store.dealer_id);
    }
  } catch (_) {}

  // 4. h6.selectedStoreName — last resort for dealership name
  if (!context.dealership) {
    try {
      const el = document.querySelector('h6.selectedStoreName');
      if (el) context.dealership = el.textContent?.trim();
    } catch (_) {}
  }

  return context;
};
